import React from 'react';
import { Layout, Menu, Avatar, Dropdown, Spin, Button, BackTop, Breadcrumb, Drawer, Popover } from 'antd';
import {
  HomeOutlined,
  ClockCircleOutlined,
  AccountBookOutlined,
  EditOutlined,
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  LogoutOutlined,
  VerticalAlignTopOutlined,
  HighlightOutlined,
  BulbOutlined,
  BellOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  ClockCircleFilled,
  AccountBookFilled,
  SearchOutlined,
  EditFilled,
  ReadOutlined,
  CalendarOutlined,
  BookOutlined,
  BgColorsOutlined,
  CheckOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  CrownOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { Link, withRouter } from 'react-router-dom';

// 业务logo
import timeLogo from '../assets/time_logo.png';
import billLogo from '../assets/bill_logo.png';
import yuxieLogo from '../assets/yuxie_logo.png';
import readLogo from '../assets/read_logo.png';
import liveLogo from '../assets/live_logo.png';
import publishLogo from '../assets/publish_logo.png';

import axios from '../common/api/axios';
import userUtil from '../common/util/UserUtil';
import Login from '../common/login/Login';
import SpotlightSearch from '../components/SpotlightSearch';
import GlobalFeedbackButton from '../components/GlobalFeedbackButton';
import UserPreferencesPanel from '../components/UserPreferencesPanel';
import ModuleAccessGuide from '../components/ModuleAccessGuide';
import {
  applyUserPreferences,
  readUserPreferences,
  saveUserPreferences,
} from '../common/util/userPreferences';
import {
  getModuleAccess,
  getModuleAccessByPath,
} from '../common/util/moduleAccess';

import './AppLayout.css';
import UserContext from '../contexts/UserContext';

const { Header, Sider, Content } = Layout;

const ACCENT_STORAGE_KEY = 'sj_accent_color';
const BRAND_ACCENT_KEY = 'blue';
const SHENJI_TECH_URL = 'https://i.yuxie.vip/shenji.html';

const ACCENT_PALETTES = [
  {
    key: 'blue',
    name: '天空蓝',
    color: '#2196F3',
    vars: {
      '--sj-primary-50': '#E3F2FD',
      '--sj-primary-100': '#BBDEFB',
      '--sj-primary-200': '#90CAF9',
      '--sj-primary-300': '#64B5F6',
      '--sj-primary-400': '#42A5F5',
      '--sj-primary-500': '#2196F3',
      '--sj-primary-600': '#1E88E5',
      '--sj-primary-700': '#1976D2',
      '--sj-primary-800': '#1565C0',
      '--sj-primary-900': '#0D47A1',
      '--sj-primary': '#2196F3',
      '--sj-primary-light': '#E3F2FD',
      '--sj-primary-dark': '#1976D2',
      '--sj-info': '#2196F3',
      '--sj-bg-hover': 'rgba(33, 150, 243, 0.08)',
      '--sj-bg-active': 'rgba(33, 150, 243, 0.15)',
      '--sj-border-focus': '#42A5F5',
      '--sj-shadow-sm': '0 2px 8px rgba(33, 150, 243, 0.08)',
      '--sj-shadow-md': '0 4px 16px rgba(33, 150, 243, 0.12)',
      '--sj-shadow-lg': '0 8px 24px rgba(33, 150, 243, 0.16)',
    },
  },
];

const getAccentPalette = (key) => ACCENT_PALETTES.find(item => item.key === key) || ACCENT_PALETTES[0];

const ACCENT_DARK_VARS = {
  blue: {
    '--sj-primary-50': '#0F2334',
    '--sj-primary-100': '#12324C',
    '--sj-primary-200': '#174A72',
    '--sj-primary-300': '#21679A',
    '--sj-primary-400': '#3B8ED0',
    '--sj-primary-500': '#64B5F6',
    '--sj-primary-600': '#7BC4FF',
    '--sj-primary-700': '#90CAF9',
    '--sj-primary-800': '#B7DDFF',
    '--sj-primary-900': '#D8EFFF',
    '--sj-primary': '#64B5F6',
    '--sj-primary-light': '#15283B',
    '--sj-primary-dark': '#90CAF9',
    '--sj-bg-hover': 'rgba(100, 181, 246, 0.08)',
    '--sj-bg-active': 'rgba(100, 181, 246, 0.15)',
    '--sj-border-focus': '#64B5F6',
  },
};

const applyAccentPalette = (key) => {
  const palette = getAccentPalette(key);
  const root = document.documentElement;
  const isDark = root.getAttribute('data-theme') === 'dark';
  const vars = {
    ...palette.vars,
    ...(isDark ? ACCENT_DARK_VARS[palette.key] : {}),
  };
  Object.entries(vars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
  root.style.setProperty('--sj-accent-current', palette.color);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', palette.color);
};

// 全屏页面路由配置：业务模块统一进入用户中心侧边栏。
const FULLPAGE_ROUTES = [];

// 全屏页面标题映射
const FULLPAGE_TITLE_MAP = {
  '/time': '时间统计',
  '/bill': '剑飞记账',
  '/ai-chat': 'AI 对话',
  '/write': '语写',
  '/write-article': '我的文章',
  '/write-read': '剑飞阅读',
  '/write-live': '人生规划',
  '/write-publish': '剑飞出书',
  '/shenji.html': '神技科技',
};

// 获取全屏页面标题
const getFullpageTitle = (pathname) => {
  const matchedKey = Object.keys(FULLPAGE_TITLE_MAP).find(
    key => pathname === key || pathname.startsWith(key + '/')
  );
  return matchedKey ? FULLPAGE_TITLE_MAP[matchedKey] : '语写';
};

// 面包屑路由映射表
const BREADCRUMB_MAP = {
  '/': { name: '首页', icon: <HomeOutlined /> },
  '/time': { name: '时间统计', icon: <ClockCircleOutlined /> },
  '/time/report': { name: '报告详情', icon: <ClockCircleOutlined /> },
  '/bill': { name: '剑飞记账', icon: <AccountBookOutlined /> },
  '/write': { name: '语写', icon: <EditOutlined /> },
  '/write-article': { name: '我的文章', icon: <EditOutlined /> },
  '/write-read': { name: '剑飞阅读', icon: <ReadOutlined /> },
  '/write-live': { name: '人生规划', icon: <CalendarOutlined /> },
  '/write-publish': { name: '剑飞出书', icon: <BookOutlined /> },
  '/ai-chat': { name: 'AI 对话', icon: <BulbOutlined /> },
  '/shenji.html': { name: '神技科技', icon: <CrownOutlined /> },
};

const GUEST_INTRO_PRODUCTS = [
  { key: 'write', title: '语写', desc: '用语音和文字沉淀思考、复盘与文章素材。', icon: <EditOutlined />, tone: 'cyan' },
  { key: 'time', title: '时间统计', desc: '记录时间流向，自动形成月报、季报和年报。', icon: <ClockCircleOutlined />, tone: 'blue' },
  { key: 'bill', title: '剑飞记账', desc: '整理收支、预算和消费结构，长期看清财务习惯。', icon: <AccountBookOutlined />, tone: 'green' },
  { key: 'read', title: '剑飞阅读', desc: '管理阅读记录和书单，把输入变成可复用的知识。', icon: <ReadOutlined />, tone: 'violet' },
  { key: 'life', title: '人生规划', desc: '按年规划目标、回顾关键节点，留下成长轨迹。', icon: <CalendarOutlined />, tone: 'amber' },
  { key: 'ai', title: 'AI 对话', desc: '使用自己的 API Key，把记录、写作和复盘接入 AI。', icon: <BulbOutlined />, tone: 'pink' },
];

class AppLayout extends React.Component {
  state = {
    user: {},
    collapsed: false,
    loading: true,
    loginModal: null,
    isMobile: false,
    drawerVisible: false,
    spotlightVisible: false,
    fabOpen: false,
    notifications: [],
    accentColor: 'blue',
    preferencesVisible: false,
    preferences: null,
  };

  componentDidMount() {
    // 初始化会话：处理 URL 中的 JSESSIONID/token/userNum，持久化到 localStorage
    userUtil.initSession();
    this.loadUser();
    // 确保 window.app.showLogin 可用
    window.app = window.app || {};
    window.app.showLogin = (onLogin) => {
      this.state.loginModal && this.state.loginModal.show(onLogin);
    };

    // 侧边栏折叠记忆
    this.initSidebarCollapsed();

    // 暗色模式初始化
    this.initTheme();

    // 移动端检测
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);

    // 键盘快捷键监听
    this.handleKeyDown = (e) => {
      const isMeta = e.ctrlKey || e.metaKey;
      const key = e.key?.toLowerCase() || '';
      if (isMeta && key === 'k') {
        e.preventDefault();
        this.setState({ spotlightVisible: true });
      }
      if (key === 'escape') {
        this.setState({ spotlightVisible: false });
      }
      if (key === '?' && !this.isInputActive()) {
        e.preventDefault();
        this.setState({ spotlightVisible: true });
      }
    };
    window.addEventListener('keydown', this.handleKeyDown);

    // 全局弹窗键盘支持：Esc关闭、Enter确认
    this.bindModalKeyboardGlobal();

    // 移动端输入框防键盘遮挡
    this.bindMobileInputScroll();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    window.removeEventListener('keydown', this.handleKeyDown);
    if (this._modalKeyboardTimer) clearInterval(this._modalKeyboardTimer);
    if (this._inputFocusHandler) {
      document.removeEventListener('focusin', this._inputFocusHandler);
    }
  }

  isInputActive() {
    const tag = document.activeElement?.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable;
  }

  initTheme = () => {
    try {
      const preferences = applyUserPreferences(readUserPreferences());
      localStorage.setItem(ACCENT_STORAGE_KEY, BRAND_ACCENT_KEY);
      applyAccentPalette(BRAND_ACCENT_KEY);
      this.setState({
        isDarkMode: document.documentElement.getAttribute('data-theme') === 'dark',
        accentColor: BRAND_ACCENT_KEY,
        preferences,
      });
    } catch {
      // ignore
    }
  };

  toggleTheme = () => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const preferences = saveUserPreferences({
      ...readUserPreferences(),
      themeMode: isDark ? 'light' : 'dark',
    });
    applyAccentPalette(BRAND_ACCENT_KEY);
    this.setState({
      isDarkMode: document.documentElement.getAttribute('data-theme') === 'dark',
      preferences,
    });
  };

  selectAccentColor = () => {
    applyAccentPalette(BRAND_ACCENT_KEY);
    try {
      localStorage.setItem(ACCENT_STORAGE_KEY, BRAND_ACCENT_KEY);
    } catch {
      // ignore
    }
    this.setState({ accentColor: BRAND_ACCENT_KEY });
  };

  renderAccentPicker = () => {
    const { accentColor } = this.state;

    return (
      <div className="sj-accent-panel">
        <div className="sj-accent-panel-title">网站主色调</div>
        <div className="sj-accent-panel-desc">当前站点统一使用 #2196F3 品牌蓝。</div>
        <div className="sj-accent-swatches">
          {ACCENT_PALETTES.map(item => (
            <button
              key={item.key}
              type="button"
              className={`sj-accent-swatch ${accentColor === item.key ? 'sj-accent-swatch-active' : ''}`}
              style={{ '--swatch-color': item.color }}
              onClick={() => this.selectAccentColor(item.key)}
              title={item.name}
            >
              <span className="sj-accent-swatch-dot" />
              <span className="sj-accent-swatch-name">{item.name}</span>
              {accentColor === item.key && <CheckOutlined className="sj-accent-swatch-check" />}
            </button>
          ))}
        </div>
      </div>
    );
  };

  renderUnauthenticatedEntry = () => (
    <section className="sj-guest-intro" aria-label="神技科技介绍">
      <div className="sj-guest-hero">
        <div className="sj-guest-eyebrow">神技科技 · 个人成长工具矩阵</div>
        <h1>把每天的记录，变成长期可见的成长资产</h1>
        <p>
          神技科技围绕语写、时间统计、记账、阅读、人生规划、出书和 AI 对话，
          提供一套统一的用户中心，让记录、复盘、创作和管理在同一个入口里持续发生。
        </p>
        <div className="sj-guest-actions">
          <Button
            type="primary"
            size="large"
            onClick={() => window.app && window.app.showLogin && window.app.showLogin()}
            className="sj-guest-login-btn"
          >
            立即登录
          </Button>
          <a className="sj-guest-link" href="https://yuxie.vip" target="_blank" rel="noopener noreferrer">
            了解语写官网
          </a>
        </div>
      </div>

      <div className="sj-guest-product-grid" aria-label="神技科技产品">
        {GUEST_INTRO_PRODUCTS.map(item => (
          <article className={`sj-guest-product-card sj-guest-product-card-${item.tone}`} key={item.key}>
            <div className="sj-guest-product-icon">{item.icon}</div>
            <div>
              <h2>{item.title}</h2>
              <p>{item.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );

  checkMobile = () => {
    const isMobile = window.innerWidth <= 768;
    this.setState({ isMobile });
  };

  bindModalKeyboardGlobal = () => {
    // 轮询检测新出现的 Modal，绑定键盘事件
    this._modalKeyboardTimer = setInterval(() => {
      document.querySelectorAll('.ant-modal-wrap:not([data-sj-kb])').forEach((wrap) => {
        wrap.setAttribute('data-sj-kb', '1');
        wrap.setAttribute('tabindex', '-1');
        wrap.focus();
        wrap.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            const cancelBtn = wrap.querySelector('.ant-modal-confirm-btns .ant-btn:not(.ant-btn-primary), .ant-modal-footer .ant-btn:not(.ant-btn-primary)');
            if (cancelBtn) cancelBtn.click();
          }
          if (e.key === 'Enter' && !e.target?.tagName?.match(/TEXTAREA/)) {
            const okBtn = wrap.querySelector('.ant-modal-confirm-btns .ant-btn-primary, .ant-modal-footer .ant-btn-primary');
            if (okBtn && document.activeElement !== okBtn) {
              okBtn.click();
            }
          }
        });
        // 焦点自动落到主操作按钮
        setTimeout(() => {
          const okBtn = wrap.querySelector('.ant-modal-confirm-btns .ant-btn-primary, .ant-modal-footer .ant-btn-primary');
          if (okBtn) okBtn.focus();
        }, 100);
      });
    }, 300);
  };

  bindMobileInputScroll = () => {
    if (window.innerWidth > 768) return;
    this._inputFocusHandler = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const rect = e.target.getBoundingClientRect();
          if (rect.top < 80) {
            window.scrollBy({ top: rect.top - 80, behavior: 'smooth' });
          }
        }, 300);
      }
    };
    document.addEventListener('focusin', this._inputFocusHandler);
  };

  loadUser = () => {
    return axios.get('/api/wx/mp/user', {
      params: { userNum: userUtil.getUserNum(), needAllVip: true },
      skipLoginModal: true
    }).then(res => {
      window.userInfo = res || {};
      this.setState({ user: res || {}, loading: false });
    }).catch(() => {
      this.setState({ loading: false });
    });
  };

  loginOut = () => {
    const appId = window.app?.appId || 'wxe27fd0d4f0978882';
    const biz = window.app?.biz || 'yuxie';
    axios.post('/api/login/loginOut', null, {
      params: { appId, biz }
    }).then(data => {
      userUtil.clearLogin();
      if (data) window.location.reload();
    }).catch(() => {
      userUtil.clearLogin();
      window.location.reload();
    });
  };

  setLoginModal = (ins) => {
    if (ins && ins !== this.state.loginModal) {
      this.setState({ loginModal: ins });
    }
  };

  initSidebarCollapsed = () => {
    try {
      const saved = localStorage.getItem('sj_sidebar_collapsed');
      if (saved !== null) {
        this.setState({ collapsed: saved === 'true' });
      }
    } catch {
      // ignore
    }
  };

  saveSidebarCollapsed = (collapsed) => {
    try {
      localStorage.setItem('sj_sidebar_collapsed', String(collapsed));
    } catch {
      // ignore
    }
  };

  toggleCollapsed = () => {
    const next = !this.state.collapsed;
    this.setState({ collapsed: next });
    this.saveSidebarCollapsed(next);
  };

  toggleDrawer = () => {
    this.setState({ drawerVisible: !this.state.drawerVisible });
  };

  closeDrawer = () => {
    this.setState({ drawerVisible: false });
  };

  handleFabClick = (module) => {
    const { user } = this.state;
    const isOnDashboard = window.location.pathname === '/' || window.location.pathname === '/index.html';
    const access = getModuleAccess(user, module);

    if (!access.isAllowed) {
      this.props.history.push(module);
      this.setState({ fabOpen: false });
      return;
    }

    if (isOnDashboard) {
      window.location.hash = module;
    } else {
      this.props.history.push({ pathname: '/', hash: module });
    }

    window.app.activeModule = module;
    const event = new CustomEvent('moduleChange', { detail: { module } });
    window.dispatchEvent(event);
    this.setState({ fabOpen: false });
  };

  // 根据当前路由生成面包屑项
  buildBreadcrumbItems = (pathname) => {
    const paths = pathname.split('/').filter(Boolean);
    const items = [
      {
        key: 'home',
        title: <Link to="/"><HomeOutlined /> 首页</Link>,
      }
    ];

    let currentPath = '';
    paths.forEach((segment) => {
      currentPath += `/${segment}`;
      const config = BREADCRUMB_MAP[currentPath];
      if (config) {
        items.push({
          key: currentPath,
          title: <Link to={currentPath}>{config.icon} {config.name}</Link>,
        });
      } else {
        // 动态路由段（如 ID）不单独展示，或根据上级推断
        const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/')) || '/';
        const parentConfig = BREADCRUMB_MAP[parentPath];
        if (parentConfig && !items.find(i => i.key === parentPath)) {
          items.push({
            key: currentPath,
            title: <span>{parentConfig.icon} {parentConfig.name}</span>,
          });
        }
      }
    });

    // 如果当前页就是首页，只保留一项
    if (pathname === '/') {
      return [{
        key: 'home',
        title: <span><HomeOutlined /> 首页</span>,
      }];
    }

    // 最后一项改为非链接（当前页）
    if (items.length > 1) {
      const last = items[items.length - 1];
      const lastTitle = BREADCRUMB_MAP[last.key]?.name || last.title;
      items[items.length - 1] = {
        key: last.key,
        title: lastTitle,
      };
    }

    return items;
  };

  render() {
    const { user, collapsed, loading, isMobile, drawerVisible } = this.state;
    const { location, children } = this.props;
    const { pathname } = location;

    // 功能页面全屏模式（避免和各页面内部 Layout/ProLayout 冲突）
    const isFullPage = FULLPAGE_ROUTES.some(route => pathname.startsWith(route));
    const currentAccess = getModuleAccessByPath(user, pathname);
    const showAccessGuide = !loading
      && currentAccess
      && currentAccess.status !== 'free'
      && !currentAccess.isAllowed
      && pathname !== '/'
      && pathname !== '/index.html'
      && !pathname.startsWith('/pay/');

    const renderAccessGuide = () => (
      <ModuleAccessGuide
        access={currentAccess}
        onLogin={() => window.app && window.app.showLogin && window.app.showLogin()}
        onPay={() => this.props.history.push(currentAccess.payPath)}
        onHome={() => this.props.history.push('/')}
      />
    );

    const handleMenuClick = (e) => {
      if (window.app) {
        e.domEvent.preventDefault();
        const module = e.key === '/' ? null : e.key;
        window.app.activeModule = module;

        this.props.history.push(module || '/');

        const event = new CustomEvent('moduleChange', { detail: { module } });
        window.dispatchEvent(event);
      }
    };

    const SIDER_GUIDE_LINKS = [
      { key: 'shenji-tech', label: '神技科技', href: SHENJI_TECH_URL, external: true, sameTab: true, icon: <CrownOutlined /> },
      { key: 'official', label: '语写官网', href: 'https://yuxie.vip', external: true, icon: <GlobalOutlined /> },
      { key: 'help', label: '使用帮助', href: '/help', internal: true, icon: <QuestionCircleOutlined /> },
      { key: 'feedback', label: '问题反馈', href: '/feedback', internal: true, icon: <BulbOutlined /> },
    ];

    const menuItems = [
      { key: '/write', icon: <img src={yuxieLogo} alt="语写" style={{ width: 16, height: 16, objectFit: 'contain' }} />, label: <span>语写</span> },
      { key: '/time', icon: <img src={timeLogo} alt="时间统计" style={{ width: 16, height: 16, objectFit: 'contain' }} />, label: <span>时间统计</span> },
      { key: '/write-read', icon: <img src={readLogo} alt="剑飞阅读" style={{ width: 16, height: 16, objectFit: 'contain' }} />, label: <span>剑飞阅读</span> },
      { key: '/write-live', icon: <img src={liveLogo} alt="人生规划" style={{ width: 16, height: 16, objectFit: 'contain' }} />, label: <span>人生规划</span> },
      { key: '/write-publish', icon: <img src={publishLogo} alt="剑飞出书" style={{ width: 16, height: 16, objectFit: 'contain' }} />, label: <span>剑飞出书</span> },
      { key: '/bill', icon: <img src={billLogo} alt="剑飞记账" style={{ width: 16, height: 16, objectFit: 'contain' }} />, label: <span>剑飞记账</span> },
      { key: '/write-article', icon: <EditOutlined />, label: <span>我的文章</span> },
      { key: '/ai-chat', icon: <BulbOutlined />, label: <span>AI 对话</span> },
    ];

    const userMenuOverlay = (
      <Menu onClick={this.loginOut}>
        <Menu.Item key="logout" icon={<LogoutOutlined />}>退出登录</Menu.Item>
      </Menu>
    );

    // 全屏页面只保留最小化顶部导航（返回首页 + 标题），避免和各页面内部 Header 重复
    if (isFullPage) {
      return (
        <UserContext.Provider value={{ user, loading, refreshUser: this.loadUser }}>
          <div className="sj-fullpage-header">
            <div className="sj-fullpage-header-left">
              <Link to="/" className="sj-fullpage-back-link">
                ← 用户中心
              </Link>
              <span className="sj-fullpage-divider">|</span>
              <span className="sj-fullpage-title">
                {getFullpageTitle(pathname)}
              </span>
            </div>
            <div className="sj-fullpage-header-right">
              <Button
                id="sj-theme-toggle"
                type="text"
                icon={this.state.isDarkMode ? <BulbOutlined /> : <HighlightOutlined />}
                onClick={this.toggleTheme}
                className="sj-header-theme-btn"
                title={this.state.isDarkMode ? '切换浅色模式' : '切换暗色模式'}
                aria-label={this.state.isDarkMode ? '切换浅色模式' : '切换暗色模式'}
              />
              <Popover
                content={this.renderAccentPicker()}
                trigger="click"
                placement="bottomRight"
                overlayClassName="sj-accent-popover"
              >
                <Button
                  type="text"
                  icon={<BgColorsOutlined />}
                  className="sj-header-theme-btn sj-header-accent-btn"
                  title="选择网站主色调"
                  aria-label="选择网站主色调"
                />
              </Popover>
              <Login getInstance={this.setLoginModal} />
              {loading ? <Spin size="small" /> : (
                <Dropdown overlay={userMenuOverlay} placement="bottomRight">
                  <div className="sj-fullpage-user">
                    <Avatar src={userUtil.avatarUrl(user.avatarUrl)} size="small" className="sj-avatar-brand">
                      {user.nickname?.[0]}
                    </Avatar>
                    <span className="sj-fullpage-nickname">{user.nickname || '未登录'}</span>
                    <DownOutlined style={{ fontSize: 11, color: 'var(--sj-text-tertiary)' }} />
                  </div>
                </Dropdown>
              )}
            </div>
          </div>
          <div className="sj-fullpage-content">
            {/* 开发模式下让子组件自己处理未登录状态（显示 UnauthenticatedLanding），生产环境才拦截 */}
            {(() => {
              try {
                const CONFIG = require('../config').default;
                if (CONFIG.DEV_MODE) return children;
              } catch (e) { /* ignore */ }
              if (showAccessGuide) return renderAccessGuide();
              if (!loading && !(user && user.userNum) && pathname !== '/' && pathname !== '/index.html' && pathname !== '/shenji.html' && !pathname.startsWith('/pay/') && pathname !== '/help' && pathname !== '/feedback') {
                return this.renderUnauthenticatedEntry();
              }
              return children;
            })()}
            {!isMobile && (
              <BackTop className="sj-back-top">
                <div className="sj-back-top-inner">
                  <VerticalAlignTopOutlined />
                </div>
              </BackTop>
            )}
            <GlobalFeedbackButton user={user} />
          </div>
        </UserContext.Provider>
      );
    }

    const isAiChatPage = pathname.startsWith('/ai-chat');
    const isWritePage = pathname.startsWith('/write');
    const moduleRootPaths = new Set(menuItems.map(item => item.key));
    const showBackButton = pathname !== '/' &&
      pathname !== '/index.html' &&
      !moduleRootPaths.has(pathname) &&
      !pathname.startsWith('/pay/');

    const breadcrumbItems = this.buildBreadcrumbItems(pathname);

    const { fabOpen } = this.state;

    const siderContent = (
      <>
        <Link
          to="/"
          className={collapsed ? 'sj-sider-logo sj-sider-logo-collapsed' : 'sj-sider-logo'}
          title="返回首页"
          aria-label="返回首页"
          onClick={isMobile ? this.closeDrawer : undefined}
        >
          <span className={collapsed ? 'sj-sider-logo-text sj-sider-logo-text-collapsed' : 'sj-sider-logo-text'}>
            {collapsed ? '首' : '首页'}
          </span>
        </Link>
        <Menu
          mode="inline"
          selectedKeys={[window.app.activeModule || pathname]}
          className="sj-sider-menu"
          items={[...menuItems]}
          onClick={(e) => {
            handleMenuClick(e);
            if (isMobile) this.closeDrawer();
          }}
        />
        {!collapsed && (
          <div className="sj-sider-guide" id="common-entry-guide">
            <div className="sj-sider-guide-title">常用入口</div>
            {SIDER_GUIDE_LINKS.map(item => (
              item.external ? (
                <a
                  key={item.key}
                  className="sj-sider-guide-item"
                  href={item.href}
                  target={item.sameTab ? undefined : '_blank'}
                  rel={item.sameTab ? undefined : 'noopener noreferrer'}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              ) : (
                <Link key={item.key} className="sj-sider-guide-item" to={item.href}>
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              )
            ))}
          </div>
        )}
        <div className="sj-sider-footer">
          <button
            type="button"
            className="sj-sider-tool"
            onClick={() => this.setState({ preferencesVisible: true })}
            title="设置"
            aria-label="打开侧栏设置"
          >
            <SettingOutlined />
            {!collapsed && <span>设置</span>}
          </button>
          <div
            className="sj-sider-logout"
            onClick={(user && user.userNum) ? this.loginOut : () => window.app?.showLogin?.()}
            title={(user && user.userNum) ? '退出登录' : '立即登录'}
          >
            {(user && user.userNum) ? <LogoutOutlined /> : <LoginOutlined />}
            {!collapsed && <span>{(user && user.userNum) ? '退出登录' : '立即登录'}</span>}
          </div>
        </div>
      </>
    );

    return (
      <UserContext.Provider value={{ user, loading, refreshUser: this.loadUser }}>
      <Layout className="sj-app-layout">
        {!isMobile && (
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={176}
            className="sj-app-sider"
          >
            {siderContent}
          </Sider>
        )}
        {isMobile && (
          <Drawer
            placement="left"
            closable={false}
            onClose={this.closeDrawer}
            open={drawerVisible}
            width={220}
            bodyStyle={{ padding: 0 }}
            className="sj-app-drawer"
          >
            {siderContent}
          </Drawer>
        )}
        <Layout className="sj-main-layout">
          <Header className="sj-app-header">
            <div className="sj-header-left">
              {isMobile ? (
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  onClick={this.toggleDrawer}
                  className="sj-header-menu-btn sj-mobile-menu-btn"
                  title="打开菜单"
                  aria-label="打开菜单"
                />
              ) : (
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={this.toggleCollapsed}
                  className="sj-header-menu-btn"
                  title={collapsed ? '展开侧边栏' : '收起侧边栏'}
                  aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
                />
              )}
              <span className="sj-header-title">
                {pathname === '/' || pathname === '/index.html'
                  ? '首页'
                  : menuItems.find(m => m.key === pathname)?.label?.props?.children || '用户中心'}
              </span>
            </div>
            <div className="sj-header-right">
              <Button
                type="text"
                icon={<SearchOutlined />}
                onClick={() => this.setState({ spotlightVisible: true })}
                className="sj-header-search-btn"
                title="搜索 (Cmd+K)"
                aria-label="搜索"
              />
              <Button
                type="text"
                icon={<BellOutlined />}
                className="sj-header-notify-btn"
                title="通知中心"
                aria-label="通知中心"
              />
              <Button
                id="sj-theme-toggle"
                type="text"
                icon={this.state.isDarkMode ? <BulbOutlined /> : <HighlightOutlined />}
                onClick={this.toggleTheme}
                className="sj-header-theme-btn"
                title={this.state.isDarkMode ? '切换浅色模式' : '切换暗色模式'}
                aria-label={this.state.isDarkMode ? '切换浅色模式' : '切换暗色模式'}
              />
              <Popover
                content={this.renderAccentPicker()}
                trigger="click"
                placement="bottomRight"
                overlayClassName="sj-accent-popover"
              >
                <Button
                  type="text"
                  icon={<BgColorsOutlined />}
                  className="sj-header-theme-btn sj-header-accent-btn"
                  title="选择网站主色调"
                  aria-label="选择网站主色调"
                />
              </Popover>
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={() => this.setState({ preferencesVisible: true })}
                className="sj-header-theme-btn sj-header-settings-btn"
                title="设置"
                aria-label="打开设置"
                id="user-settings-button"
              />
              <Login
                getInstance={this.setLoginModal}
              />
              {loading ? (
                <Spin size="small" />
              ) : (
                <Dropdown overlay={userMenuOverlay} placement="bottomRight">
                  <div className="sj-header-user">
                    <Avatar
                      src={userUtil.avatarUrl(user.avatarUrl)}
                      size="small"
                      className="sj-avatar-brand"
                    >
                      {user.nickname?.[0]}
                    </Avatar>
                    <span className="sj-header-nickname">
                      {user.nickname || '未登录'}
                    </span>
                    <DownOutlined className="sj-header-arrow" />
                  </div>
                </Dropdown>
              )}
            </div>
          </Header>
          {!isWritePage && (
            <div className="sj-breadcrumb-wrap">
              {showBackButton && (
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => this.props.history.goBack()}
                  className="sj-back-btn"
                >
                  返回
                </Button>
              )}
              <Breadcrumb items={breadcrumbItems} className="sj-breadcrumb" />
            </div>
          )}
          <Content
            className={`sj-main-content ${
              pathname === '/' || pathname === '/index.html' ? 'sj-main-content-dashboard' : ''
            } ${isAiChatPage ? 'sj-main-content-ai-chat' : ''} ${isWritePage ? 'sj-main-content-write' : ''}`}
          >
            {/* 开发模式下让子组件自己处理未登录状态 */}
            {(() => {
              try {
                const CONFIG = require('../config').default;
                if (CONFIG.DEV_MODE) return children;
              } catch (e) { /* ignore */ }
              if (showAccessGuide) return renderAccessGuide();
              if (!loading && !(user && user.userNum) && pathname !== '/' && pathname !== '/index.html' && pathname !== '/shenji.html' && !pathname.startsWith('/pay/') && pathname !== '/help' && pathname !== '/feedback') {
                return this.renderUnauthenticatedEntry();
              }
              return children;
            })()}
          </Content>
          {!isMobile && (
            <BackTop className="sj-back-top">
              <div className="sj-back-top-inner">
                <VerticalAlignTopOutlined />
              </div>
            </BackTop>
          )}
          {/* 快捷操作浮窗 FAB */}
          {!isMobile && !isAiChatPage && (
            <div className={`sj-fab ${fabOpen ? 'sj-fab-open' : ''}`}>
              <div className="sj-fab-actions">
                <Button
                  type="primary"
                  shape="round"
                  icon={<ClockCircleFilled />}
                  onClick={() => this.handleFabClick('/time')}
                  aria-label="记录时间"
                  title="记录时间"
                  className="sj-fab-action"
                >
                  <span>记录时间</span>
                </Button>
                <Button
                  type="primary"
                  shape="round"
                  icon={<EditFilled />}
                  onClick={() => this.handleFabClick('/write')}
                  aria-label="写一篇语写"
                  title="写一篇语写"
                  className="sj-fab-action"
                >
                  <span>写语写</span>
                </Button>
                <Button
                  type="primary"
                  shape="round"
                  icon={<AccountBookFilled />}
                  onClick={() => this.handleFabClick('/bill')}
                  aria-label="记一笔账"
                  title="记一笔账"
                  className="sj-fab-action"
                >
                  <span>记账</span>
                </Button>
              </div>
              <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                onClick={() => this.setState({ fabOpen: !fabOpen })}
                className="sj-fab-trigger"
                aria-label="快捷新建"
                title={fabOpen ? '收起快捷新建' : '快捷新建'}
                id="quick-create-button"
              />
              {!fabOpen && <div className="sj-fab-hint">快捷新建</div>}
            </div>
          )}
          <SpotlightSearch
            visible={this.state.spotlightVisible}
            onClose={() => this.setState({ spotlightVisible: false })}
          />
          <UserPreferencesPanel
            visible={this.state.preferencesVisible}
            onClose={() => this.setState({ preferencesVisible: false })}
          />
          <GlobalFeedbackButton user={user} />
        </Layout>
      </Layout>
      </UserContext.Provider>
    );
  }
}

export default withRouter(AppLayout);
